import { useState } from "react";
import { Copy, Eye, EyeOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { trpc } from "../../trpc";

type WebhookSecretRowProps = {
  secretPreview: string | null;
};

const WebhookSecretRow = ({ secretPreview }: WebhookSecretRowProps): JSX.Element => {
  const utils = trpc.useUtils();
  const [secret, setSecret] = useState<string | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const revealSecret = trpc.webhooks.revealSecret.useMutation({
    onSuccess: (data) => setSecret(data.secret),
    onError: (error) => {
      toast.error("No se pudo mostrar el secreto", { description: error.message });
    },
  });

  const rotateSecret = trpc.webhooks.rotateSecret.useMutation({
    onSuccess: async (data) => {
      setSecret(data.secret);
      setIsConfirmOpen(false);
      await utils.webhooks.get.invalidate();
      toast.success("Secreto nuevo generado", {
        description: "Actualízalo en tu servidor: los próximos eventos ya llegan con este secreto.",
      });
    },
    onError: (error) => {
      toast.error("No se pudo generar el secreto", { description: error.message });
    },
  });

  const handleCopy = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      toast.success("Secreto copiado", { description: "Se copió al portapapeles", duration: 2000 });
    } catch {
      toast.error("No se pudo copiar", { duration: 2000 });
    }
  };

  const handleRotate = (e: React.MouseEvent) => {
    e.preventDefault();
    rotateSecret.mutate();
  };

  const hasSecret = secretPreview !== null || secret !== null;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium leading-none">Secreto</p>
      <div className="flex flex-col gap-2 md:flex-row md:items-center">
        <div className="min-h-10 min-w-0 flex-1 break-all rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm">
          {secret ?? secretPreview ?? "Sin secreto"}
        </div>
        <div className="flex flex-wrap gap-2">
          {hasSecret &&
            (secret ? (
              <>
                <Button type="button" variant="ghost" className="min-h-11" onClick={handleCopy}>
                  <Copy />
                  Copiar
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="min-h-11"
                  onClick={() => setSecret(null)}
                >
                  <EyeOff />
                  Ocultar
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="ghost"
                className="min-h-11"
                disabled={revealSecret.isLoading}
                onClick={() => revealSecret.mutate()}
              >
                <Eye />
                Mostrar
              </Button>
            ))}
          <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
            <AlertDialogTrigger asChild>
              <Button type="button" variant="ghost" className="min-h-11">
                <RefreshCw />
                {hasSecret ? "Rotar secreto" : "Generar secreto"}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {hasSecret ? "¿Rotar el secreto?" : "¿Generar un secreto?"}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {hasSecret
                    ? "El secreto actual deja de funcionar en este momento. Los próximos eventos llegarán con el secreto nuevo, así que actualízalo en tu servidor enseguida."
                    : "Los próximos eventos llegarán con el secreto nuevo en el encabezado X-Palomma-Secret. Configúralo en tu servidor para verificar que vienen de Palomma."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel type="button" disabled={rotateSecret.isLoading}>
                  Cancelar
                </AlertDialogCancel>
                <AlertDialogAction
                  type="button"
                  className={
                    hasSecret
                      ? "bg-red-500 text-white hover:border-red-600 hover:bg-red-600 hover:text-white"
                      : undefined
                  }
                  disabled={rotateSecret.isLoading}
                  onClick={handleRotate}
                >
                  {rotateSecret.isLoading
                    ? "Generando…"
                    : hasSecret
                      ? "Rotar secreto"
                      : "Generar secreto"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
};

export default WebhookSecretRow;
